import React, { useEffect, useState, useRef } from "react";
import { Arrow, debounce } from "../../helpers";
import "./pager.scss";
import { EventData, useSwipeable } from "react-swipeable";
import { Person, Pager } from "../HallOfFame/types";
import { EventEmitter } from "fbemitter";
const emitter = new EventEmitter();

export const pagerListener = ({
  callback
}: {
  callback?: ({
    dir,
    data,
    offsetValue
  }: {
    dir: string;
    data?: Record<string, unknown>;
    offsetValue: number;
  }) => void;
}) => {
  return emitter.addListener(
    "pagerDirection",
    ({
      dir,
      data,
      offsetValue
    }: {
      dir: string;
      data: Record<string, unknown>;
      offsetValue: number;
    }) => {
      if (callback) callback({ dir, data, offsetValue });
    }
  );
};

const pagerEmitter = ({
  dir,
  data,
  offsetValue
}: {
  dir: string;
  data?: Record<string, unknown>;
  offsetValue: number;
}) => {
  emitter.emit("pagerDirection", { dir, data, offsetValue });
};

export default ({
  children,
  tileWidth = 230,
  tilePadding = 10,
  tileClassName,
  fadedEdge = false,
  hidePager = false,
  sectionLink = {
    name: "",
    url: "#",
    target: "_self"
  },
  triggerUpdate,
  customHookFlag,
  customHook,
  data
}: {
  children?: JSX.Element;
  tileWidth?: number;
  tilePadding?: number;
  tileClassName?: string;
  fadedEdge?: boolean;
  hidePager?: boolean;
  customHookFlag?: Record<string, unknown> | number | boolean;
  customHook?: Function;
  data?: Record<string, unknown>;
  sectionLink?: {
    name: string;
    url: string;
    target: "_blank" | "_self" | "_parent";
  };
  triggerUpdate?: Record<string, unknown>[] | Person[] | boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffest] = useState<Pager>({
    value: 0,
    excess: 0,
    initValue: 0,
    pager: {
      next: false,
      prev: false
    }
  });

  const handlers = useSwipeable({
    onSwiped: (eventData: EventData) => {
      if (eventData.dir === "Left" && offset.pager.next) {
        handleNext();
      } else if (eventData.dir === "Right" && offset.pager.prev) {
        handlePrev();
      }
    }
  });

  let tilesWrapper = ref.current;

  const func = () => {
    tilesWrapper = ref.current;
    if (tilesWrapper !== null) {
      const allTiles =
        tilesWrapper && tilesWrapper.querySelectorAll(`.${tileClassName}`);
      if (tilesWrapper !== null && allTiles && allTiles.length > 0) {
        let allTilesWidth = Array.from(allTiles).reduce(
          (total: number, tile: HTMLDivElement) => {
            let width = total + (tile.clientWidth + tilePadding);
            return width;
          },
          0
        );
        const condition = allTilesWidth > tilesWrapper.clientWidth;
        const pager = {
          next: true,
          prev: false
        };
        const noPager = {
          next: false,
          prev: false
        };
        setOffest({
          ...offset,
          initValue: allTilesWidth - tilesWrapper.clientWidth,
          value: 0,
          pager: condition ? pager : noPager
        });
      }
    }
  };

  useEffect(() => {
    func();
  }, [triggerUpdate]);

  useEffect(() => {
    customHook &&
      customHook({
        tilesWrapperWidth: tilesWrapper ? tilesWrapper.clientWidth : 0,
        tileWidth,
        offsetValue: offset.value / tileWidth,
        tilePadding,
        handleNext,
        handlePrev,
        excess: offset.excess
      });
  }, [customHookFlag]);

  useEffect(() => {
    func();
    debounce({
      func: () => {
        func();
        customHook &&
          customHook({
            tilesWrapperWidth: tilesWrapper ? tilesWrapper.clientWidth : 0,
            tileWidth,
            offsetValue: offset.value / tileWidth,
            tilePadding,
            handleNext,
            handlePrev,
            excess: offset.excess,
            screenResize: true
          });
      }
    });
  }, []);

  const handleNext = (disableEmitter: boolean = false) => {
    tilesWrapper = ref.current;
    const allTiles =
      tilesWrapper && tilesWrapper.querySelectorAll(`.${tileClassName}`);
    if (tilesWrapper && allTiles) {
      let allTilesWidth = Array.from(allTiles).reduce(
        (total: number, tile: HTMLDivElement) => {
          let width = total + (tile.clientWidth + tilePadding);
          return width;
        },
        0
      );
      let condition =
        allTilesWidth - (offset.value + tileWidth) > tilesWrapper.clientWidth;
      let excess = allTilesWidth - offset.value - tilesWrapper.clientWidth;
      if (condition) {
        setOffest({
          ...offset,
          value: offset.value + tileWidth,
          pager: { ...offset.pager, prev: true }
        });
      } else if (excess && offset.pager.next) {
        setOffest({
          ...offset,
          excess,
          value: offset.value + excess,
          pager: { prev: true, next: false }
        });
      }
      !disableEmitter &&
        pagerEmitter({
          dir: "next",
          data,
          offsetValue: offset.value / tileWidth
        });
    }
  };

  const handlePrev = (disableEmitter: boolean = false) => {
    if (offset.excess) {
      setOffest({
        ...offset,
        value: offset.value - offset.excess,
        excess: 0,
        pager: { prev: offset.value > tileWidth, next: true }
      });
    } else if (offset.pager.prev) {
      setOffest({
        ...offset,
        value: offset.value - tileWidth,
        pager: { next: true, prev: offset.value !== tileWidth }
      });
    }
    !disableEmitter &&
      pagerEmitter({
        dir: "prev",
        data,
        offsetValue: offset.value / tileWidth
      });
  };

  return offset.pager.next || offset.pager.prev || sectionLink ? (
    <div
      className={`paginator${
        fadedEdge && offset.pager.next ? " fade-next" : ""
      }`}
    >
      <div
        {...handlers}
        ref={ref}
        className="paginator-tiles"
        style={{ transform: `translateX(-${offset.value}px)` }}
      >
        <div style={{ display: "flex" }} {...handlers}>
          {children}
        </div>
      </div>
      <div className={`paginator__footer${hidePager ? " hide" : ""}`}>
        {sectionLink.name && (
          <a
            className="paginator__btn"
            target={sectionLink.target}
            href={sectionLink.url}
          >
            {sectionLink.name}
          </a>
        )}
        <div className="paginator__pager">
          <button
            onClick={() => handlePrev()}
            className={`prev${offset.pager.prev ? "" : " hide"}`}
          >
            Prev
            <Arrow fill="#000000" />
          </button>
          <button
            onClick={() => handleNext()}
            className={`next${offset.pager.next ? "" : " hide"}`}
          >
            Next
            <Arrow fill="#000000" />
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className={`paginator`}>
      <div
        {...handlers}
        ref={ref}
        className="paginator-tiles"
        style={{ transform: `translateX(-${offset.value}px)` }}
      >
        <div style={{ display: "flex" }} {...handlers}>
          {children}
        </div>
      </div>
    </div>
  );
};
